import type React from "react";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { isAnyModalOpen } from "@/components/ModalChrome";
import { OverlayScroll } from "@/components/OverlayScroll";
import type { Command } from "./CommandPaletteContext";
import { useCommandPalette } from "./CommandPaletteContext";

const IS_MAC = navigator.platform.toUpperCase().includes("MAC");
const SHORTCUT_HINT = IS_MAC ? "⌘K" : "Ctrl+K";

type FlatHeader = { type: "header"; label: string };
type FlatCommand = {
  type: "command";
  index: number;
  id: string;
  label: string;
  shortcut?: string;
  action: () => void;
};
type FlatItem = FlatHeader | FlatCommand;

function fuzzyMatch(query: string, text: string): boolean {
  const lower = text.toLowerCase();
  const terms = query.toLowerCase().split(/\s+/);
  return terms.every((term) => lower.includes(term));
}

function groupBy(commands: Command[]): Map<string, Command[]> {
  const groups = new Map<string, Command[]>();
  for (const cmd of commands) {
    const key = cmd.group ?? "";
    const list = groups.get(key) ?? [];
    list.push(cmd);
    groups.set(key, list);
  }
  return groups;
}

function toFlatCommand(cmd: Command, index: number): FlatCommand {
  return {
    type: "command",
    index,
    id: cmd.id,
    label: cmd.label,
    shortcut: cmd.shortcut,
    action: cmd.action,
  };
}

function buildFlatList(commands: Command[]): FlatItem[] {
  const groups = groupBy(commands);
  const result: FlatItem[] = [];
  let idx = 0;

  for (const [group, cmds] of groups) {
    if (group) result.push({ type: "header", label: group });
    for (const cmd of cmds) {
      result.push(toFlatCommand(cmd, idx));
      idx++;
    }
  }

  return result;
}

// The highlighted row follows the selection: the arrow keys walk past the
// visible window, so the list is nudged just far enough to show it again.
function scrollSelectionIntoView(list: HTMLDivElement | null) {
  const el = list?.querySelector("[data-selected='true']");
  if (el) el.scrollIntoView({ block: "nearest" });
}

// A modal that came up meanwhile (an error dialog behind the palette) owns the
// screen: the command is dropped, not run underneath it.
function runAfterClose(action: () => void) {
  requestAnimationFrame(() => {
    if (!isAnyModalOpen()) action();
  });
}

function usePaletteState(
  commands: Command[],
  isOpen: boolean,
  close: () => void,
) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    return commands.filter((cmd) => fuzzyMatch(query, cmd.label));
  }, [query, commands]);

  const flatList = useMemo(() => buildFlatList(filtered), [filtered]);
  const commandCount = filtered.length;

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll when selection changes
  useEffect(() => {
    scrollSelectionIntoView(listRef.current);
  }, [selectedIndex]);

  const executeAtIndex = useCallback(
    (index: number) => {
      const command = filtered[index];
      if (!command) return;
      close();
      runAfterClose(() => command.action());
    },
    [filtered, close],
  );

  const keyActions: Record<string, () => void> = useMemo(
    () => ({
      ArrowDown: () =>
        setSelectedIndex((prev) => (prev < commandCount - 1 ? prev + 1 : 0)),
      ArrowUp: () =>
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : commandCount - 1)),
      Enter: () => executeAtIndex(selectedIndex),
      Escape: () => close(),
    }),
    [commandCount, selectedIndex, executeAtIndex, close],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const handler = keyActions[e.key];
      if (handler) {
        e.preventDefault();
        handler();
      }
    },
    [keyActions],
  );

  return {
    query,
    setQuery,
    selectedIndex,
    setSelectedIndex,
    listRef,
    flatList,
    handleKeyDown,
    executeAtIndex,
  };
}

function SearchInput({
  query,
  onChange,
}: {
  query: string;
  onChange: (value: string) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
  });

  return (
    <SearchRow>
      <SearchIconSpan>⌕</SearchIconSpan>
      <SearchField
        ref={ref as React.RefObject<HTMLInputElement>}
        value={query}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type a command…"
        spellCheck={false}
        autoComplete="off"
        data-testid="command-palette-input"
      />
      <ShortcutBadge>{SHORTCUT_HINT}</ShortcutBadge>
    </SearchRow>
  );
}

function ResultItem({
  item,
  isSelected,
  onSelect,
  onExecute,
}: {
  item: FlatCommand;
  isSelected: boolean;
  onSelect: () => void;
  onExecute: () => void;
}) {
  return (
    <ResultRow
      $selected={isSelected}
      data-selected={isSelected}
      onClick={onExecute}
      onMouseEnter={onSelect}
    >
      <ResultLabel>{item.label}</ResultLabel>
      {item.shortcut && <ResultShortcut>{item.shortcut}</ResultShortcut>}
    </ResultRow>
  );
}

export function CommandPalette() {
  const { isOpen, commands, close } = useCommandPalette();

  const state = usePaletteState(commands, isOpen, close);

  if (!isOpen) return null;

  return (
    <Backdrop onClick={close} data-testid="command-palette-backdrop">
      <Dialog
        onClick={(e) => e.stopPropagation()}
        onKeyDown={state.handleKeyDown}
        data-testid="command-palette"
      >
        <SearchInput query={state.query} onChange={state.setQuery} />

        <ResultsScroll>
          <ResultsList ref={state.listRef}>
            {state.flatList.length === 0 && (
              <EmptyState>No matching commands</EmptyState>
            )}
            {state.flatList.map((item) =>
              item.type === "header" ? (
                <GroupHeader key={`header:${item.label}`}>
                  {item.label}
                </GroupHeader>
              ) : (
                <ResultItem
                  key={item.id}
                  item={item}
                  isSelected={item.index === state.selectedIndex}
                  onSelect={() => state.setSelectedIndex(item.index)}
                  onExecute={() => state.executeAtIndex(item.index)}
                />
              ),
            )}
          </ResultsList>
        </ResultsScroll>

        <Footer>
          <FooterHint>
            <Kbd>↑↓</Kbd> navigate <Kbd>↵</Kbd> select <Kbd>esc</Kbd> close
          </FooterHint>
        </Footer>
      </Dialog>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 15vh;
`;

const Dialog = styled.div`
  width: 480px;
  max-height: 420px;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 10px;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
`;

const SearchIconSpan = styled.span`
  font-size: 16px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  flex-shrink: 0;
  line-height: 1;
`;

const SearchField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${(p) => p.theme.semanticTextBody};
  font-family: inherit;

  &::placeholder {
    color: ${(p) => p.theme.semanticTextPlaceholder};
  }
`;

const ShortcutBadge = styled.span`
  font-size: 10px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  background: ${(p) => p.theme.tableTheadCellBackground};
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  flex-shrink: 0;
  font-family: inherit;
`;

// The results scroll on the app's scrollbars; the list inside is what the
// selection-following scroll queries (scrollIntoView reaches the viewport
// around it).
const ResultsScroll = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
`;

const ResultsList = styled.div`
  padding: 6px;
`;

const GroupHeader = styled.div`
  padding: 8px 10px 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

const ResultRow = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  background: ${(p) =>
    p.$selected ? p.theme.sidebarListItemHoverBackground : "transparent"};
  color: ${(p) =>
    p.$selected ? p.theme.semanticTextHeading : p.theme.semanticTextBody};
`;

const ResultLabel = styled.span`
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ResultShortcut = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
  flex-shrink: 0;
  margin-left: 12px;
  font-family: inherit;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

const Footer = styled.div`
  border-top: 1px solid ${(p) => p.theme.cards1Border};
  padding: 8px 14px;
  flex-shrink: 0;
`;

const FooterHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${(p) => p.theme.semanticTextPlaceholder};
`;

const Kbd = styled.kbd`
  font-size: 10px;
  font-family: inherit;
  padding: 1px 5px;
  border-radius: 3px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  background: ${(p) => p.theme.tableTheadCellBackground};
  color: ${(p) => p.theme.semanticTextBody};
`;
